import { useAuth0 } from '@auth0/auth0-react'
import type { FC } from 'react'
import { useState } from 'react'

import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import NotificationsIcon from '@mui/icons-material/Notifications'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import FormControl from '@mui/material/FormControl'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { useProfile } from '../../account/hooks/useProfile'
import { useUpdateProfile } from '../../account/hooks/useUpdateProfile'
import type { ProfileChanges } from '../../account/types/Profile'
import { isTelefoonValid } from '../../account/util/profile-util'
import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { RELEASE_NOTES_URL } from '../../config'
import { getAuth0Options } from '../../gateways/auth0'
import { PropertyRow } from '../../theme/components/PropertyRow'

import { ProfileBeheer } from './ProfileBeheer'

export interface Props {
  onChangeUserParam: (userParam: string) => void
  userParam: string
  releaseNotesUnseen: boolean
  onReleaseNotesClick: () => void
}

const BEHEER_DEELNEMERS = /** @type {string[]} */ ['DSP', 'Mijn Aansluiting']

const profileFields: {
  key: keyof ProfileChanges
  label: string
  maxLength: number
}[] = [
  { key: 'telefoon', label: 'Telefoonnummer', maxLength: 20 },
  { key: 'rol', label: 'Rol', maxLength: 100 },
]

// de naam staat al in de header, dus alleen als invoerveld tonen
const editableFields: typeof profileFields = [
  { key: 'name', label: 'Naam', maxLength: 100 },
  ...profileFields,
]

const TELEFOON_HINT =
  'Alleen cijfers, spaties, ( ) en -, eventueel met + ervoor'

export const Profile: FC<Props> = ({
  onChangeUserParam,
  userParam,
  releaseNotesUnseen,
  onReleaseNotesClick,
}) => {
  const { user, logout } = useAuth0()
  const { returnTo } = getAuth0Options()
  const { data: profile, isLoading, isError } = useProfile()
  const [changes, setChanges] = useState<ProfileChanges | null>(null)
  const { mutate: updateProfile, isPending } = useUpdateProfile()
  const telefoonValid = !changes || isTelefoonValid(changes.telefoon)

  const { combi, deelnemer, delegerende, switchUserParam } =
    useAuthenticatedUser()
  const isBeheer =
    switchUserParam &&
    !!deelnemer &&
    BEHEER_DEELNEMERS.includes(deelnemer) &&
    !combi

  return (
    <Card sx={{ width: 350, overflowWrap: 'anywhere' }}>
      <CardHeader
        avatar={<Avatar aria-label="avatar" src={user?.picture || ''}></Avatar>}
        title={profile?.name || user?.name}
        subheader={combi ? `Combi: ${combi}` : `Deelnemer: ${deelnemer}`}
      />
      <CardContent>
        {delegerende && (
          <FormControl sx={{ width: '100%', pb: 1 }}>
            <TextField
              variant="standard"
              fullWidth
              label="Gedelegeerd door"
              value={delegerende}
              slotProps={{
                input: { readOnly: true },
              }}
            />
          </FormControl>
        )}

        <Stack spacing={1} sx={{ pb: 1 }}>
          <PropertyRow label="Email" value={profile?.email ?? user?.email} />
          {changes
            ? editableFields.map(({ key, label, maxLength }) => (
                <TextField
                  key={key}
                  variant="standard"
                  fullWidth
                  label={label}
                  value={changes[key]}
                  onChange={({ target }) =>
                    setChanges({ ...changes, [key]: target.value })
                  }
                  slotProps={{ htmlInput: { maxLength } }}
                  {...(key === 'telefoon' && {
                    error: !telefoonValid,
                    helperText: TELEFOON_HINT,
                  })}
                />
              ))
            : profile &&
              profileFields.map(({ key, label }) => (
                <PropertyRow key={key} label={label} value={profile[key]} />
              ))}
          {isLoading &&
            profileFields.map(({ key }) => (
              <Skeleton key={key} data-testid="accountSkeleton" height={48} />
            ))}
          {isError && (
            <Alert severity="warning">
              Accountgegevens konden niet worden geladen
            </Alert>
          )}
        </Stack>
        {isBeheer && (
          <FormControl sx={{ width: '100%', pb: 1 }}>
            <ProfileBeheer
              onChangeUserParam={onChangeUserParam}
              userParam={userParam}
            />
          </FormControl>
        )}
      </CardContent>

      <List disablePadding>
        <ListItemButton
          component={'a'}
          href={RELEASE_NOTES_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onReleaseNotesClick}
          data-testid="releaseNotesLink"
        >
          <ListItemIcon>
            <Badge variant="dot" color="error" invisible={!releaseNotesUnseen}>
              <NotificationsIcon />
            </Badge>
          </ListItemIcon>
          <ListItemText primary="Release notes" />
        </ListItemButton>
      </List>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {changes ? (
          <>
            <Button onClick={() => setChanges(null)}>Annuleren</Button>
            <Button
              variant="contained"
              onClick={() =>
                updateProfile(changes, { onSuccess: () => setChanges(null) })
              }
              disabled={isPending || !telefoonValid}
            >
              Opslaan
            </Button>
          </>
        ) : (
          <>
            {profile && (
              <Button
                onClick={() =>
                  setChanges({
                    name: profile.name,
                    telefoon: profile.telefoon,
                    rol: profile.rol,
                  })
                }
              >
                Gegevens wijzigen
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ExitToAppIcon />}
              onClick={() => logout({ logoutParams: { returnTo } })}
            >
              Uitloggen
            </Button>
          </>
        )}
      </CardActions>
    </Card>
  )
}
